import './images/main.scss'

import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import { definePrimeVueComponents } from './utils/definePrimeVueComponents'
import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'
import PrimeVue from 'primevue/config'
import DialogService from 'primevue/dialogservice'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import { useAuthenticationStore } from './features/authentication/presentation/stores/useAuthenticationStore'
import { Tooltip } from 'primevue'
import { useCurrentUserStore } from './features/currentUser/presentation/stores/currentUserStore'
import { addCurrentUserInfoCase } from './features/currentUser/domain/usecases/addCurrentUserInfoUsecase'
import { enterClick } from './directives/enterClick'
import { createHead } from '@unhead/vue'
import ContextMenu from '@imengyu/vue3-context-menu'
import { registerServiceWorker, requestNotificationPermission } from './utils/serviceWorker'
import * as L from 'leaflet'

const app = createApp(App)

app.use(createPinia())
app.use(definePrimeVueComponents)
app.use(DialogService)
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)
app.use(ContextMenu)

const head = createHead()
app.use(head)

const myPreset = definePreset(Aura, {
  semantic: {
    primary: {
      100: '#00ceff',
      200: '#00ceff',
      300: '#00ceff',
      400: '#00ceff',
      500: '#00ceff',
      600: '#00ceff',
      700: '#00ceff',
      800: '#00ceff',
      900: '#00ceff'
    },
    orange: {
      400: '#d3625d'
    },
    colorScheme: {
      light: {
        primary: {
          color: '#00ceff'
        }
      },
      dark: {
        primary: {
          color: '#00ceff',
          inverseColor: '#00ceff',
          hoverColor: '#e99349',
          activeColor: '#00ceff'
        }
      }
    }
  }
})

app.use(PrimeVue, {
  ripple: true,
  theme: {
    preset: myPreset,
    options: {
      darkModeSelector: '.p-dark',
      cssLayer: false
    }
  }
})

const authenticationStore = useAuthenticationStore()
authenticationStore.setAuthenticationData()

const currentUserStore = useCurrentUserStore()
const currentPath = window.location.pathname

if (authenticationStore.authenticationData.accessToken && currentPath !== '/auth-check') {
  await addCurrentUserInfoCase()
}

const storeInitWatch = watch(
  () => currentUserStore.allPolicies,
  async (newValue) => {
    if (newValue !== undefined) {
      const { default: router } = await import('./router')
      app.use(router)
      app.mount('#app')
      storeInitWatch()
    }
  },
  { immediate: true }
)
app.directive('can', (el, binding) => {
  const updateDisplay = () => {
    if (el && binding.value) {
      const contains = (authorizeMeta: string[], userRole: object) =>
        authorizeMeta.some((el) => el in userRole)
      const authorizes = Object.fromEntries(binding.value?.map((key: string) => [key, true]))
      if (contains(currentUserStore.allPolicies ?? [], authorizes)) {
        el.style.display = ''
      } else {
        el.style.display = 'none'
      }
    }
  }
  updateDisplay()

  const unwatch = watch(() => currentUserStore.allPolicies, updateDisplay, {
    deep: true,
    immediate: true
  })

  el._cleanup = unwatch
})

app.directive('enter-click', enterClick)

registerServiceWorker().then(() => {
  requestNotificationPermission()
})
;(function patchLeafletAnimateZoom() {
  const PATCH_FLAG = '__tt_animateZoomPatched__'
  // The Leaflet ESM namespace object is immutable, so we cannot add custom
  // properties directly onto `L`. Store the flag on the global object instead.
  const globalObj = globalThis as any
  if (globalObj[PATCH_FLAG]) return

  L.DivOverlay.include({
    _animateZoom(this: any, e: { zoom: number; center: L.LatLng }) {
      if (!this._map || !this._map._latLngToNewLayerPoint) return
      // call the original implementation to preserve scale/rotation
      if (typeof this.__tt_originalAnimateZoom === 'function') {
        return this.__tt_originalAnimateZoom(e)
      }
      // fallback: do nothing if original not stored
    }
  })

  // Preserve original Marker._animateZoom (could be patched by plugins)
  const originalMarkerAnimateZoom = (L.Marker.prototype as any)._animateZoom
  L.Marker.include({
    __tt_originalAnimateZoom: originalMarkerAnimateZoom,
    _animateZoom(this: any, e: { zoom: number; center: L.LatLng }) {
      if (!this._map || !this._map._latLngToNewLayerPoint) return
      return originalMarkerAnimateZoom.call(this, e)
    }
  })
  globalObj[PATCH_FLAG] = true
})()
